import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { dashboardAPI } from '@/utils/api';
import { Users, ClipboardList, Video, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [statsRes, remindersRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getReminders()
      ]);
      
      setStats(statsRes.data);
      setReminders(remindersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage translators, projects, and work approvals</p>
        </div>
        
        {/* Reminders */}
        {reminders.length > 0 && (
          <div className="space-y-3">
            {reminders.map((reminder, index) => (
              <Alert 
                key={index}
                className={`border-l-4 ${
                  reminder.priority === 'high' 
                    ? 'border-l-accent bg-accent/5' 
                    : 'border-l-warning bg-warning/5'
                }`}
              >
                <AlertCircle className={`h-5 w-5 ${
                  reminder.priority === 'high' ? 'text-accent' : 'text-warning'
                }`} />
                <AlertDescription className="ml-2">
                  <strong>{reminder.type === 'approval_reminder' ? 'Pending Approvals' : 'Reminder'}:</strong>{' '}
                  {reminder.message}
                </AlertDescription>
              </Alert>
            ))}
          </div>
        )}
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-accent hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Translators</CardTitle>
              <Users className="w-5 h-5 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{stats?.total_translators || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Active vendors</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-warning hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending Submissions</CardTitle>
              <ClipboardList className="w-5 h-5 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{stats?.pending_submissions || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting approval</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-creative hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Videos</CardTitle>
              <Video className="w-5 h-5 text-creative" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{stats?.total_videos || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">In system</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-success hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Assigned Videos</CardTitle>
              <Video className="w-5 h-5 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{stats?.assigned_videos || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Currently active</p>
            </CardContent>
          </Card>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <a 
                href="/admin/translators" 
                className="block p-4 rounded-lg border border-border hover:border-accent hover:bg-accent/5 transition-colors"
              >
                <h4 className="font-semibold text-primary mb-1">Manage Translators</h4>
                <p className="text-sm text-muted-foreground">Add, edit, or view translator profiles</p>
              </a>
              <a 
                href="/admin/projects" 
                className="block p-4 rounded-lg border border-border hover:border-accent hover:bg-accent/5 transition-colors"
              >
                <h4 className="font-semibold text-primary mb-1">Manage Projects</h4>
                <p className="text-sm text-muted-foreground">Create clients, projects, and videos</p>
              </a>
              <a 
                href="/admin/assignments" 
                className="block p-4 rounded-lg border border-border hover:border-accent hover:bg-accent/5 transition-colors"
              >
                <h4 className="font-semibold text-primary mb-1">Assign Work</h4>
                <p className="text-sm text-muted-foreground">Assign videos to translators</p>
              </a>
              <a 
                href="/admin/submissions" 
                className="block p-4 rounded-lg border border-border hover:border-accent hover:bg-accent/5 transition-colors"
              >
                <h4 className="font-semibold text-primary mb-1">Review Submissions</h4>
                <p className="text-sm text-muted-foreground">Approve translator work submissions</p>
              </a>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>System Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">Active Translators</span>
                  <span className="text-lg font-bold text-primary">{stats?.total_translators || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">Pending Approvals</span>
                  <span className="text-lg font-bold text-warning">{stats?.pending_submissions || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">Videos in System</span>
                  <span className="text-lg font-bold text-creative">{stats?.total_videos || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
